import type { ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { Cluster } from "@/components/layout/cluster";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { LoadingState, ErrorState, EmptyState } from "@/components/feedback/states";
import { type JobRunFile, useJobRun, useJobRunFiles } from "@/api/jobs";
import { usePipelineNames } from "@/api/pipelines";
import { useLiveInvalidate } from "@/lib/live";
import { fmtBytes, fmtDuration, fmtNumber } from "@/lib/format";

export const Route = createFileRoute("/_app/jobs/$jobId/runs/$runId")({
  component: JobRunDetail,
});

function JobRunDetail() {
  const { jobId, runId } = Route.useParams();
  const navigate = useNavigate();
  const run = useJobRun(runId);
  const files = useJobRunFiles(runId);
  const names = usePipelineNames();
  useLiveInvalidate([
    ["jobs", "run", runId],
    ["jobs", "run-files", runId],
  ]);

  if (run.isLoading) return <LoadingState />;
  if (run.error || !run.data)
    return <ErrorState error={run.error ?? "Not found"} onRetry={() => run.refetch()} />;
  const r = run.data;
  const running = ["RUNNING", "QUEUED", "STARTING"].includes((r.status ?? "").toUpperCase());
  const filesDone = (files.data ?? []).filter((f) =>
    ["SUCCESS", "COMPLETED", "FAILED", "SKIPPED"].includes((f.status ?? "").toUpperCase()),
  ).length;
  const filesTotal = r.filesTotal ?? files.data?.length ?? 0;

  const columns: Column<JobRunFile>[] = [
    { id: "file", header: "File", cell: (f) => f.filename ?? f.id, sortValue: (f) => f.filename },
    {
      id: "size",
      header: "Size",
      align: "right",
      cell: (f) => (f.sizeBytes != null ? fmtBytes(f.sizeBytes) : "—"),
    },
    {
      id: "pipeline",
      header: "Matched pipeline",
      cell: (f) =>
        f.matchedPipelineId ? (names[f.matchedPipelineId] ?? f.matchedPipelineId) : "— unmatched",
    },
    { id: "status", header: "Status", cell: (f) => <StatusChip status={f.status ?? "PENDING"} /> },
    {
      id: "rows",
      header: "Rows",
      align: "right",
      cell: (f) => `${fmtNumber(f.recordsRead)} → ${fmtNumber(f.recordsWritten)}`,
    },
    {
      id: "dur",
      header: "Duration",
      align: "right",
      cell: (f) => (f.durationMs != null ? fmtDuration(f.durationMs) : "—"),
    },
  ];

  return (
    <PageContainer maxWidth={960}>
      <PageHeader
        title={`Run · ${runId.slice(0, 8)}`}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: "Jobs", to: "/jobs" },
              { label: "Job", to: `/jobs/${jobId}` },
              { label: runId.slice(0, 8) },
            ]}
          />
        }
      />

      {running ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Processing files — {filesDone} of {filesTotal || "?"}
          </Typography>
          <LinearProgress
            variant={filesTotal ? "determinate" : "indeterminate"}
            value={filesTotal ? (filesDone / filesTotal) * 100 : undefined}
          />
        </Box>
      ) : null}

      <Card sx={{ p: 2.5, mb: 3 }}>
        <Cluster gap={4} align="start">
          <Field label="Status">
            <StatusChip status={r.status} />
          </Field>
          <Field label="Trigger">{r.triggeredBy ?? r.triggerType ?? "—"}</Field>
          <Field label="Files">
            {r.filesMatched ?? 0}/{r.filesTotal ?? 0} matched
          </Field>
          <Field label="Pipelines">
            {fmtNumber(r.pipelinesRun)} run · {fmtNumber(r.pipelinesFailed)} failed
          </Field>
          <Field label="Started">
            <RelativeTime value={r.triggeredAt} />
          </Field>
        </Cluster>
        {r.errorMessage ? (
          <Typography variant="body2" color="error.main" sx={{ mt: 1.5 }}>
            {r.errorMessage}
          </Typography>
        ) : null}
      </Card>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Files
      </Typography>
      <Card>
        <DataTable
          columns={columns}
          data={files.data}
          isLoading={files.isLoading}
          error={files.error}
          onRetry={() => files.refetch()}
          rowKey={(f) => f.id}
          onRowClick={(f) =>
            f.matchedPipelineId && f.pipelineExecutionId
              ? navigate({
                  to: "/pipelines/$pipelineId/runs/$runId",
                  params: { pipelineId: f.matchedPipelineId, runId: f.pipelineExecutionId },
                })
              : undefined
          }
          empty={<EmptyState title="No files" description="This run processed no files." />}
        />
      </Card>
    </PageContainer>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}
